// Componente "Registro de hoje", 4 abas (Espiritual/Família/Saúde/Propósito).
//
// Cada aba tem um formulário enxuto com os campos descritos na FASE 2:
//   • Espiritual: capítulos lidos, minutos de oração, gratidão, devocional
//   • Família:    minutos com a família, notas
//   • Saúde:      pressão (sist/diast), peso, sono, exercícios, água
//   • Propósito:  notas (metas/projetos via componente separado)
//
// O upsert é por categoria: cada aba envia POST /api/journey/log com apenas
// os campos preenchidos. O backend faz merge, não sobrescreve campos vazios.
//
// Mobile-first: tabs viram pills horizontais em telas pequenas.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Save, Sparkles, Heart, Activity, Target, Flame } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { journeyApi } from "../lib/journeyApi";

const TABS = [
  { key: "spiritual", label: "Espiritual", Icon: Sparkles, gold: true },
  { key: "family", label: "Família", Icon: Heart },
  { key: "health", label: "Saúde", Icon: Activity },
  { key: "purpose", label: "Propósito", Icon: Target },
];

function FieldLabel({ children }) {
  return (
    <span className="text-[10px] uppercase tracking-[0.25em] text-navy/55 block mb-1.5">
      {children}
    </span>
  );
}

function numericOrEmpty(v) {
  if (v === "" || v === null || v === undefined) return "";
  return String(v);
}

function toNumber(v) {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function buildPayload(category, form) {
  // Strip empty strings; only send fields the user actually typed.
  const data = {};
  if (category === "spiritual") {
    if (form.bible_chapters !== "") data.bible_chapters = toNumber(form.bible_chapters);
    if (form.prayer_minutes !== "") data.prayer_minutes = toNumber(form.prayer_minutes);
    if (form.gratitude?.trim()) data.gratitude = form.gratitude.trim();
    if (form.devotional_done !== "") data.devotional_done = !!form.devotional_done;
  } else if (category === "family") {
    if (form.family_minutes !== "") data.family_minutes = toNumber(form.family_minutes);
    if (form.family_notes?.trim()) data.family_notes = form.family_notes.trim();
  } else if (category === "health") {
    if (form.bp_systolic !== "") data.bp_systolic = toNumber(form.bp_systolic);
    if (form.bp_diastolic !== "") data.bp_diastolic = toNumber(form.bp_diastolic);
    if (form.weight_kg !== "") data.weight_kg = toNumber(form.weight_kg);
    if (form.sleep_hours !== "") data.sleep_hours = toNumber(form.sleep_hours);
    if (form.exercise_minutes !== "") data.exercise_minutes = toNumber(form.exercise_minutes);
    if (form.water_cups !== "") data.water_cups = toNumber(form.water_cups);
  } else if (category === "purpose") {
    if (form.purpose_notes?.trim()) data.purpose_notes = form.purpose_notes.trim();
  }
  return data;
}

function SpiritualForm({ form, setForm }) {
  return (
    <div className="space-y-4" data-testid="journey-form-spiritual">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Capítulos lidos</FieldLabel>
          <Input
            type="number"
            min={0}
            max={200}
            value={numericOrEmpty(form.bible_chapters)}
            onChange={(e) => setForm({ ...form, bible_chapters: e.target.value })}
            placeholder="0"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-bible-chapters"
          />
        </div>
        <div>
          <FieldLabel>Oração (min)</FieldLabel>
          <Input
            type="number"
            min={0}
            max={600}
            value={numericOrEmpty(form.prayer_minutes)}
            onChange={(e) => setForm({ ...form, prayer_minutes: e.target.value })}
            placeholder="0"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-prayer-minutes"
          />
        </div>
      </div>
      <div>
        <FieldLabel>Hoje sou grato por…</FieldLabel>
        <Textarea
          rows={3}
          maxLength={2000}
          value={form.gratitude || ""}
          onChange={(e) => setForm({ ...form, gratitude: e.target.value })}
          placeholder="Liste 1 a 3 motivos de gratidão."
          className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          data-testid="journey-gratitude"
        />
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!form.devotional_done}
          onChange={(e) => setForm({ ...form, devotional_done: e.target.checked })}
          data-testid="journey-devotional-done"
          className="w-4 h-4 accent-gold"
        />
        <span className="text-sm text-navy">Marquei o devocional como lido</span>
      </label>
    </div>
  );
}

function FamilyForm({ form, setForm }) {
  return (
    <div className="space-y-4" data-testid="journey-form-family">
      <div>
        <FieldLabel>Tempo com a família (minutos)</FieldLabel>
        <Input
          type="number"
          min={0}
          max={1440}
          value={numericOrEmpty(form.family_minutes)}
          onChange={(e) => setForm({ ...form, family_minutes: e.target.value })}
          placeholder="0"
          className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          data-testid="journey-family-minutes"
        />
      </div>
      <div>
        <FieldLabel>Notas (metas familiares, conversas, gestos)</FieldLabel>
        <Textarea
          rows={4}
          maxLength={2000}
          value={form.family_notes || ""}
          onChange={(e) => setForm({ ...form, family_notes: e.target.value })}
          placeholder="O que aconteceu hoje? Algum aniversário, gesto, conversa, plano?"
          className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          data-testid="journey-family-notes"
        />
      </div>
    </div>
  );
}

function HealthForm({ form, setForm }) {
  return (
    <div className="space-y-4" data-testid="journey-form-health">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Pressão (sistólica)</FieldLabel>
          <Input
            type="number"
            min={50}
            max={260}
            value={numericOrEmpty(form.bp_systolic)}
            onChange={(e) => setForm({ ...form, bp_systolic: e.target.value })}
            placeholder="120"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-bp-systolic"
          />
        </div>
        <div>
          <FieldLabel>Pressão (diastólica)</FieldLabel>
          <Input
            type="number"
            min={30}
            max={180}
            value={numericOrEmpty(form.bp_diastolic)}
            onChange={(e) => setForm({ ...form, bp_diastolic: e.target.value })}
            placeholder="80"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-bp-diastolic"
          />
        </div>
        <div>
          <FieldLabel>Peso (kg)</FieldLabel>
          <Input
            type="number"
            step="0.1"
            min={20}
            max={400}
            value={numericOrEmpty(form.weight_kg)}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
            placeholder="78.5"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-weight"
          />
        </div>
        <div>
          <FieldLabel>Sono (horas)</FieldLabel>
          <Input
            type="number"
            step="0.5"
            min={0}
            max={24}
            value={numericOrEmpty(form.sleep_hours)}
            onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })}
            placeholder="7.5"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-sleep"
          />
        </div>
        <div>
          <FieldLabel>Exercícios (min)</FieldLabel>
          <Input
            type="number"
            min={0}
            max={600}
            value={numericOrEmpty(form.exercise_minutes)}
            onChange={(e) => setForm({ ...form, exercise_minutes: e.target.value })}
            placeholder="30"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-exercise"
          />
        </div>
        <div>
          <FieldLabel>Água (copos)</FieldLabel>
          <Input
            type="number"
            min={0}
            max={30}
            value={numericOrEmpty(form.water_cups)}
            onChange={(e) => setForm({ ...form, water_cups: e.target.value })}
            placeholder="8"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            data-testid="journey-water"
          />
        </div>
      </div>
    </div>
  );
}

function PurposeForm({ form, setForm }) {
  return (
    <div className="space-y-4" data-testid="journey-form-purpose">
      <div>
        <FieldLabel>Objetivos do dia / projetos em andamento</FieldLabel>
        <Textarea
          rows={5}
          maxLength={2000}
          value={form.purpose_notes || ""}
          onChange={(e) => setForm({ ...form, purpose_notes: e.target.value })}
          placeholder="O que avancei hoje? O que farei amanhã?"
          className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          data-testid="journey-purpose-notes"
        />
      </div>
      <p className="text-[11px] text-navy/55 leading-relaxed">
        Metas e projetos de longo prazo ficarão disponíveis em uma futura aba.
        aqui é apenas o pensamento do dia.
      </p>
    </div>
  );
}

export default function JourneyToday({ onChanged }) {
  const [active, setActive] = useState("spiritual");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [today, setToday] = useState(null);
  const [forms, setForms] = useState({
    spiritual: {},
    family: {},
    health: {},
    purpose: {},
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await journeyApi.today();
        if (!alive) return;
        setToday(data);
        // Hidrata os formulários com o que já foi salvo hoje (edição inline).
        const next = { spiritual: {}, family: {}, health: {}, purpose: {} };
        Object.entries(data.logs || {}).forEach(([cat, log]) => {
          next[cat] = { ...(log.data || {}) };
        });
        setForms(next);
      } catch {
        /* swallow, UI mostra estado vazio */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    const data = buildPayload(active, forms[active]);
    if (Object.keys(data).length === 0) {
      toast.message("Preencha pelo menos um campo antes de salvar.");
      setSaving(false);
      return;
    }
    try {
      const saved = await journeyApi.log({ category: active, data });
      setToday((prev) => ({
        ...(prev || {}),
        logs: { ...(prev?.logs || {}), [active]: saved },
        categories_logged_today: Array.from(
          new Set([...(prev?.categories_logged_today || []), active]),
        ),
      }));
      toast.success("Registro salvo. Que dia abençoado!");
      // Refresh streak depois de cada save (pode aumentar)
      try {
        const s = await journeyApi.streak();
        setToday((prev) => ({ ...(prev || {}), streak: s }));
        onChanged?.(s);
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Não foi possível salvar agora. Tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };

  const logged = today?.categories_logged_today || [];

  return (
    <section
      className="bg-white border border-border rounded-2xl p-5 sm:p-6"
      data-testid="journey-today"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
            Registro de hoje
          </p>
          <h2 className="font-display text-2xl text-navy mt-1">Minha Jornada</h2>
        </div>
        {today?.streak?.current > 0 && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-[11px] uppercase tracking-widest text-navy"
            data-testid="journey-streak-badge"
          >
            <Flame className="w-3.5 h-3.5 text-gold" aria-hidden />
            {today.streak.current} dia
            {today.streak.current === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div
        className="mt-5 flex gap-2 overflow-x-auto -mx-1 px-1 pb-1"
        role="tablist"
        data-testid="journey-tabs"
      >
        {TABS.map((t) => {
          const Icon = t.Icon;
          const isActive = active === t.key;
          const isLogged = logged.includes(t.key);
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`journey-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[11px] uppercase tracking-widest transition-colors border ${
                isActive
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy/65 border-border hover:border-gold/60"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? "text-gold" : "text-navy/55"}`} />
              {t.label}
              {isLogged && (
                <span
                  className={`inline-flex items-center justify-center rounded-full ${
                    isActive ? "bg-gold/20" : "bg-emerald-50"
                  } w-4 h-4`}
                  aria-label="Registrado hoje"
                  data-testid={`journey-tab-check-${t.key}`}
                >
                  <Check
                    className={`w-3 h-3 ${
                      isActive ? "text-gold" : "text-emerald-600"
                    }`}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Form */}
      <div className="mt-5">
        {loading ? (
          <p className="text-sm text-navy/55">Carregando…</p>
        ) : (
          <>
            {active === "spiritual" && (
              <SpiritualForm
                form={forms.spiritual}
                setForm={(f) => setForms({ ...forms, spiritual: f })}
              />
            )}
            {active === "family" && (
              <FamilyForm
                form={forms.family}
                setForm={(f) => setForms({ ...forms, family: f })}
              />
            )}
            {active === "health" && (
              <HealthForm
                form={forms.health}
                setForm={(f) => setForms({ ...forms, health: f })}
              />
            )}
            {active === "purpose" && (
              <PurposeForm
                form={forms.purpose}
                setForm={(f) => setForms({ ...forms, purpose: f })}
              />
            )}
          </>
        )}
      </div>

      <button
        type="button"
        disabled={saving || loading}
        onClick={save}
        data-testid="journey-save"
        className="mt-5 inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors w-full sm:w-auto"
      >
        {saving ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Salvando…
          </>
        ) : (
          <>
            <Save className="w-3.5 h-3.5" /> Salvar registro de hoje
          </>
        )}
      </button>
    </section>
  );
}
